const STORAGE_KEY = "aera:latestMoneyUndo";
const EVENT_NAME = "aera:latest-money-undo";
/** Fired after actions that invalidate the remembered undo (e.g. cancel matched income). */
export const CLEAR_LATEST_MONEY_UNDO_EVENT = "aera:clear-latest-money-undo";

export type LatestMoneyUndo = {
  operationId: string;
  ownerUserId: string;
  workspaceId: string;
  label: string;
  savedAt: number;
};

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof sessionStorage !== "undefined";
}

export function readLatestMoneyUndo(): LatestMoneyUndo | null {
  if (!canUseStorage()) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LatestMoneyUndo;
    if (
      !parsed?.operationId?.trim() ||
      !parsed?.ownerUserId?.trim() ||
      !parsed?.workspaceId?.trim()
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function rememberLatestMoneyUndo(entry: LatestMoneyUndo): void {
  if (!canUseStorage()) return;
  const next: LatestMoneyUndo = {
    operationId: entry.operationId.trim(),
    ownerUserId: entry.ownerUserId.trim(),
    workspaceId: entry.workspaceId.trim(),
    label: entry.label.trim() || "ธุรกรรมล่าสุด",
    savedAt: Date.now(),
  };
  if (!next.operationId || !next.ownerUserId || !next.workspaceId) return;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
}

export function clearLatestMoneyUndo(): void {
  if (!canUseStorage()) return;
  sessionStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
}

/** Ask the app shell to drop the sticky undo bar (safe to call from data-table). */
export function requestClearLatestMoneyUndo(detail?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(CLEAR_LATEST_MONEY_UNDO_EVENT, { detail: detail ?? null }),
  );
}

/** Clear only when the remembered undo targets this operation (e.g. undo from history tab). */
export function clearLatestMoneyUndoIfMatches(operationId: string): void {
  const entry = readLatestMoneyUndo();
  if (!entry) return;
  if (entry.operationId.trim() !== operationId.trim()) return;
  clearLatestMoneyUndo();
}

export function subscribeLatestMoneyUndo(
  listener: (entry: LatestMoneyUndo | null) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => listener(readLatestMoneyUndo());
  const clearHandler = () => {
    clearLatestMoneyUndo();
  };
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener(CLEAR_LATEST_MONEY_UNDO_EVENT, clearHandler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener(CLEAR_LATEST_MONEY_UNDO_EVENT, clearHandler);
    window.removeEventListener("storage", handler);
  };
}
